import React, { useEffect, useRef } from 'react';

const DEFAULT_EQ_FREQS = [32, 64, 125, 250, 500, 1000, 2000];

export default function AudioEqualizer({
  stream,
  defaultMasterGain = 2,
  eqFrequencies = DEFAULT_EQ_FREQS,
}) {
  const pumpRef = useRef(null);
  const audioCtxRef = useRef(null);
  const eqFiltersRef = useRef([]);
  const masterGainRef = useRef(null);

  useEffect(() => {
    if (!stream || !pumpRef.current) return;

    pumpRef.current.srcObject = stream;

    const audioCtx = new AudioContext();
    audioCtxRef.current = audioCtx;

    const source = audioCtx.createMediaStreamSource(stream);
    const filters = eqFrequencies.map(freq => {
      const f = audioCtx.createBiquadFilter();
      f.type = 'peaking';
      f.frequency.value = freq;
      f.Q.value = 1;
      f.gain.value = 0;
      return f;
    });

    if (filters.length) {
      source.connect(filters[0]);
      filters.reduce((prev, curr) => {
        prev.connect(curr);
        return curr;
      });
    }

    const masterGain = audioCtx.createGain();
    masterGain.gain.value = defaultMasterGain;
    masterGainRef.current = masterGain;

    (filters.length ? filters[filters.length - 1] : source)
      .connect(masterGain)
      .connect(audioCtx.destination);

    eqFiltersRef.current = filters;

    if (audioCtx.state === 'suspended') {
      const resume = () => {
        audioCtx.resume();
        document.removeEventListener('click', resume);
      };
      document.addEventListener('click', resume);
    }

    return () => {
      audioCtx.close().catch(() => {});
    };
  }, [stream, defaultMasterGain, eqFrequencies]);

  const onMasterGainChange = e => {
    const v = parseFloat(e.target.value);
    if (masterGainRef.current) masterGainRef.current.gain.value = v;
  };
  const onEqGainChange = (i, e) => {
    const v = parseFloat(e.target.value);
    if (eqFiltersRef.current[i]) eqFiltersRef.current[i].gain.value = v;
  };

  return (
    <div>
      <video
        ref={pumpRef}
        autoPlay
        muted
        style={{ display: 'none' }}
      />

      <h4>Master Volume</h4>
      <input
        type="range"
        min="0"
        max="4"
        step="0.1"
        defaultValue={defaultMasterGain}
        onChange={onMasterGainChange}
      />

      <h4>EQ Bands</h4>
      {eqFrequencies.map((freq, i) => (
        <div key={freq}>
          <label>{freq} Hz:</label>
          <input
            type="range"
            min="-15"
            max="15"
            step="0.1"
            defaultValue="0"
            onChange={e => onEqGainChange(i, e)}
          />
        </div>
      ))}
    </div>
  );
}